import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { apiService } from '../services/api';
import BlocksRenderer from '../components/BlocksRenderer';
import colors from '../theme/colors';

const PageContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 3rem 2rem;
  background-color: ${colors.mainWhite};
  min-height: calc(100vh - 200px);
`;

const PageTitle = styled.h1`
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  margin: 0 0 0.75rem 0;
  color: ${colors.mainBlack};
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.25;
  background: ${colors.mainGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const PageDescription = styled.p`
  font-size: 1.125rem;
  color: ${colors.textGray};
  margin: 0 0 1.75rem 0;
  line-height: 1.6;
`;

const PageContent = styled.div`
  font-size: 1.0625rem;
  line-height: 1.7;
  color: ${colors.textDark};

  p {
    margin-bottom: 1.25rem;
  }

  h1 { font-size: 2rem; margin: 2rem 0 0.75rem 0; font-weight: 700; }
  h2 { font-size: 1.5rem; margin: 1.75rem 0 0.6rem 0; font-weight: 600; color: ${colors.mainBlack}; }
  h3 { font-size: 1.25rem; margin: 1.5rem 0 0.5rem 0; font-weight: 600; color: ${colors.mainBlack}; }
  h4, h5, h6 { font-size: 1.125rem; margin: 1.25rem 0 0.5rem 0; font-weight: 600; }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: ${colors.textGray};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${colors.mainOrange};
  background-color: ${colors.goldGrey};
  border: 2px solid ${colors.mainOrange};
  border-radius: 8px;
  margin: 2rem 0;
`;

const Page: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPage = async () => {
      if (!slug) {
        setError('Сторінку не знайдено');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const pageData = await apiService.getPageBySlug(slug);

        // Достатньо slug + контенту; title може бути порожнім після міграції
        if (pageData && (pageData.title != null || pageData.slug)) {
          setPage(pageData);
        } else {
          setError('Сторінку не знайдено');
        }
      } catch (err: any) {
        setError(`Не вдалося завантажити сторінку. Помилка: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <PageContainer>
        <LoadingMessage>Завантаження сторінки...</LoadingMessage>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorMessage>{error}</ErrorMessage>
      </PageContainer>
    );
  }

  if (!page) {
    return (
      <PageContainer>
        <ErrorMessage>Сторінку не знайдено</ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle>{page.title || page.slug}</PageTitle>

      {page.description && (
        <PageDescription>{page.description}</PageDescription>
      )}

      <PageContent>
        {page.blocks && page.blocks.length > 0 ? (
          <BlocksRenderer blocks={page.blocks} />
        ) : (
          <p>Контент сторінки буде додано найближчим часом.</p>
        )}
      </PageContent>
    </PageContainer>
  );
};

export default Page;

